import { AnalyticsService } from '@/shared/analytics/analytics-service';
import { type BusinessWithId } from '@/shared/schema';
import { type AdminSettings } from '@/shared/schema/admin';
import {
  type Unsubscribe,
  type User,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import {
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { toast } from 'sonner';
import { create } from 'zustand';
import { auth } from '../firebase/firebase';
import {
  adminBillingCol,
  adminSettingsRef,
  ownerRef,
} from '../firebase/services/helpers/firestore-refs';

interface AuthStorage {
  user: User | null;
  profile: BusinessWithId | null;
  adminSettings: AdminSettings | null;
  hasPendingPayment: boolean;

  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initialize: () => void;
  unsubscribe: Unsubscribe | null;
  status: 'idle' | 'loading' | 'unauthenticated' | 'loggedIn' | 'error';
  error_message: string | null;
  clear_session: () => void;
  refreshProfile: () => Promise<void>;
}

export const useAuth = create<AuthStorage>((set, get) => ({
  user: null,
  profile: null,
  unsubscribe: null,
  status: 'idle',
  error_message: null,
  adminSettings: null,
  hasPendingPayment: false,

  login: async (email, password) => {
    try {
      set({ status: 'loading', error_message: null });
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e: any) {
      let errorMessage = 'Credenciales incorrectas o error de red.';
      if (
        e.code === 'auth/user-not-found' ||
        e.code === 'auth/wrong-password' ||
        e.code === 'auth/invalid-credential'
      ) {
        errorMessage = 'El correo o la contraseña son incorrectos.';
      } else if (e.code === 'auth/too-many-requests') {
        errorMessage = 'Demasiados intentos. Intenta más tarde.';
      }
      set({ status: 'error', error_message: errorMessage });
      toast.error(errorMessage);
    }
  },

  logout: async () => {
    try {
      set({ status: 'loading' });
      await auth.signOut();
      get().clear_session();
    } catch (e) {
      const error = e instanceof Error ? e.message : 'Logout Failed';
      set({ status: 'error', error_message: error });
    }
  },

  initialize: () => {
    if (get().unsubscribe) return;
    set({ status: 'loading' });

    const sub = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 [Auth] State Changed:', user?.uid || 'No user');

      if (user) {
        try {
          // 🚀 Fetch Business Profile
          console.log('📂 [Auth] Fetching Profile for:', user.uid);
          let businessSnap = await getDoc(ownerRef(user.uid));

          if (!businessSnap.exists()) {
            console.warn(
              '⚠️ [Auth] No business profile found. Creating default...',
            );

            const defaultProfile = {
              name: user.email?.split('@')[0].toUpperCase() || 'MI EMPRESA',
              email: user.email || '',
              taxId: '',
              phone: '',
              address: '',
              account_type: 'business',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              payrollSettings: {
                defaultCurrency: 'BS',
                paymentFrequency: 'biweekly',
              },
              audit: {
                createdBy: user.uid,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
            };

            await setDoc(ownerRef(user.uid), defaultProfile);
            console.log('✅ [Auth] Default Profile Created');

            // Re-fetch to get consistent data
            businessSnap = await getDoc(ownerRef(user.uid));
          }

          const businessData = {
            id: user.uid,
            ...businessSnap.data(),
          } as BusinessWithId;
          console.log('👤 [Auth] Business Data Loaded:', businessData.name);

          // 🔧 Administrative Settings
          console.log('⚙️ [Auth] Fetching Admin Settings...');
          let adminSettingsSnap = await getDoc(adminSettingsRef(user.uid));

          if (!adminSettingsSnap.exists()) {
            console.log('⚙️ [Auth] Creating default admin settings...');
            const defaultAdmin = {
              accountStatus: 'active',
              planId: 'free',
              createdAt: serverTimestamp(),
              expiresAt: null,
            };
            await setDoc(adminSettingsRef(user.uid), defaultAdmin);
            adminSettingsSnap = await getDoc(adminSettingsRef(user.uid));
          }

          const adminSettings = adminSettingsSnap.data() as AdminSettings;

          // 💳 Payments
          console.log('💳 [Auth] Checking Pending Payments...');
          let hasPendingPayment = false;
          try {
            const pendingSnap = await getDocs(
              query(
                adminBillingCol(user.uid),
                where('status', '==', 'pending'),
                limit(1),
              ),
            );
            hasPendingPayment = !pendingSnap.empty;
            console.log(
              '✅ [Auth] Payments Checked. hasPending:',
              hasPendingPayment,
            );
          } catch (billingError) {
            console.error('❌ [Auth] Billing Query Error:', billingError);
          }

          console.log('✨ [Auth] Initialization Complete. Setting State.');
          set({
            user,
            profile: businessData,
            adminSettings,
            hasPendingPayment,
            status: 'loggedIn',
          });

          AnalyticsService.identify(user.uid, {
            email: businessData.email || user.email,
            name: businessData.name,
            account_type: 'business',
          });
        } catch (error) {
          console.error('❌ [Auth] Global Initialization Error:', error);
          set({ user, profile: null, status: 'error' });
        }
      } else {
        console.log('👋 [Auth] No user session. Clearing.');
        get().clear_session();
      }
    });

    set({ unsubscribe: sub });
  },

  refreshProfile: async () => {
    const user = get().user;
    if (!user) return;
    set({ status: 'loading' });
    try {
      const businessSnap = await getDoc(ownerRef(user.uid));
      if (!businessSnap.exists()) return;
      const businessData = {
        id: user.uid,
        ...businessSnap.data(),
      } as BusinessWithId;

      set({ profile: businessData, status: 'loggedIn' });
    } catch (e) {
      console.error('Refresh Error:', e);
    }
  },

  clear_session: () => {
    set({
      profile: null,
      user: null,
      adminSettings: null,
      hasPendingPayment: false,
      status: 'unauthenticated',
      error_message: null,
    });
  },
}));
